import { useInfiniteQuery } from '@tanstack/react-query';

import { FilterValues } from '@/hooks/use-filter-values';
import { getNextPageParam } from '@/lib/constants/pagination';
import { mapTeamMemberStatusToDTO } from '@/modules/common/mappers/team-member-status.mapper';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { mapCustomAttributeFilters } from '@/modules/common/utils/custom-attribute-filters';
import { getTeamMembers } from '@/modules/team/api/team.api';
import { MemberStatus } from '@/modules/team/types/team-member';
import { RangeMode } from '@/types/filters';

import { readOptionCityId } from '../utils/city-option';

/**
 * Hook for fetching team members list with infinite scroll pagination.
 * Maps UI filter values to API request format.
 *
 * @param filters - Filter values from useFilterValues hook
 * @returns useInfiniteQuery result with team members data
 */
export const useTeamMembers = ({ filters }: { filters: FilterValues['values'] }) => {
  return useInfiniteQuery({
    queryKey: ['team-members', filters],
    queryFn: ({ pageParam }) => {
      const apiFilters = mapTeamMemberFilters(filters);
      return getTeamMembers({ cursor: pageParam, filters: apiFilters });
    },
    getNextPageParam,
    initialPageParam: undefined,
    staleTime: 10 * 10 * 1000, // 10 minutes
    select: data => {
      // Deduplicate items by id to prevent React key warnings
      const allItems = data.pages.flatMap(page => page.items);
      const uniqueItems = allItems.filter(
        (item, index, self) => index === self.findIndex(t => t.id === item.id)
      );
      return new PaginatedResponse({
        items: uniqueItems,
        pagination: data.pages[data.pages.length - 1]?.pagination,
      });
    },
  });
};

/**
 * Maps UI filter values to API request format.
 * Transforms filter names and values to match backend expectations.
 */
const mapTeamMemberFilters = (filters: FilterValues['values']) => {
  const apiFilters: Record<string, any> = {};

  // Search string
  if (filters.q) {
    apiFilters.search = filters.q;
  }

  // Status filter
  if (filters.status) {
    const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status];
    apiFilters.statuses = statusArray.map((s: MemberStatus) => mapTeamMemberStatusToDTO(s));
  }

  // Department filter
  if (filters.department) {
    const departmentArray = Array.isArray(filters.department)
      ? filters.department
      : [filters.department];
    apiFilters.departmentIds = departmentArray.map((id: string | number) => Number(id));
  }

  // Seniority filter
  if (filters.seniority) {
    const seniorityArray = Array.isArray(filters.seniority)
      ? filters.seniority
      : [filters.seniority];
    apiFilters.seniorityIds = seniorityArray.map((id: string | number) => Number(id));
  }

  // Role filter
  if (filters.role) {
    const roleArray = Array.isArray(filters.role) ? filters.role : [filters.role];
    apiFilters.roleIds = roleArray.map((id: string | number) => Number(id));
  }

  // Manager filter
  if (filters.manager) {
    const managerArray = Array.isArray(filters.manager) ? filters.manager : [filters.manager];
    apiFilters.managerIds = managerArray.map((id: string | number) => Number(id));
  }

  // Membership type filter — send enum values directly
  if (filters.membershipType) {
    apiFilters.membershipTypes = Array.isArray(filters.membershipType)
      ? filters.membershipType
      : [filters.membershipType];
  }

  // Is manager filter (switch) — URL value is the string 'true'/'false'
  if (filters.isManager === 'true' || filters.isManager === 'false') {
    apiFilters.isManager = filters.isManager === 'true';
  }

  // Capacity range filter
  if (filters.capacity && typeof filters.capacity === 'string') {
    const [mode, value] = filters.capacity.split(':');
    const numValueInMinutes = Number(value) * 60;

    if (mode === RangeMode.More && value) {
      apiFilters.capacities = [{ minCapacity: numValueInMinutes, maxCapacity: 24 * 60 }]; // Max 24 hours
    } else if (mode === RangeMode.Less && value) {
      apiFilters.capacities = [{ minCapacity: 0, maxCapacity: numValueInMinutes }];
    } else if (mode === RangeMode.Equal && value) {
      apiFilters.capacities = [{ minCapacity: numValueInMinutes, maxCapacity: numValueInMinutes }];
    }
  }

  // Handle location filters with country-city structure
  if (filters.country || filters.city) {
    const countries = filters.country
      ? Array.isArray(filters.country)
        ? filters.country
        : [filters.country]
      : [];

    const cities = filters.city
      ? Array.isArray(filters.city)
        ? filters.city
        : [filters.city]
      : [];

    // If both country and city are selected, create location filter with cities
    if (countries.length > 0 && cities.length > 0) {
      apiFilters.locations = countries.map(country => ({
        country: String(country),
        cities: cities
          .filter(cityId => readOptionCityId(cityId).country === country)
          .map(cityId => readOptionCityId(cityId).city),
      }));
    }
    // If only country is selected, filter by entire country
    else if (countries.length > 0) {
      apiFilters.locations = countries.map(country => ({
        country: String(country),
      }));
    }
    // If only city is selected, group by country
    else if (cities.length > 0) {
      // Group cities by country
      const citiesByCountry = new Map<string, string[]>();
      cities.forEach(cityId => {
        const { country, city } = readOptionCityId(cityId);
        if (!citiesByCountry.has(country)) {
          citiesByCountry.set(country, []);
        }
        citiesByCountry.get(country)?.push(city);
      });

      apiFilters.locations = Array.from(citiesByCountry.entries()).map(([country, cityList]) => ({
        country,
        cities: cityList,
      }));
    }
  }

  const customAttrFilters = mapCustomAttributeFilters(filters);
  if (customAttrFilters) {
    apiFilters.customAttributeFilters = customAttrFilters;
  }

  return apiFilters;
};
